import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useApi, ApiError } from "../api/client";
import type {
  RecommendationActionRequest,
  RecommendationStatusRow,
} from "../api/client";
import { useFramework, frameworkLabel } from "../framework";
import { StateIcon } from "./StateIcon";
import { formatDate } from "../api/formatDate";
import {
  effectiveStatus,
  statusByKey,
  type Recommendation,
  type Strength,
} from "../recommendations/derive";
import "./RecommendationsPanel.css";

/**
 * Recommendations tile body (ADR-009).
 *
 * Lists the recommendations derived from the most recent assessment and lets a
 * keyworker mark each one actioned or set it aside (with an optional note),
 * persisted append-only via POST /whanau/:id/recommendations. Protective
 * factors are shown separately as strengths to maintain (not action items).
 *
 * Derivation happens in the parent (WhanauProfile) so the tile count and this
 * panel agree; this component owns the per-item action UI and the API calls.
 */

type Props = {
  whanauId: number;
  recommendations: Recommendation[];
  strengths: Strength[];
  statuses: RecommendationStatusRow[] | undefined;
  /** Refetch the profile after a successful action. */
  onActed: () => void;
};

type Compose = { key: string; action: "actioned" | "dismissed" } | null;

export function RecommendationsPanel({
  whanauId,
  recommendations,
  strengths,
  statuses,
  onActed,
}: Props) {
  const { t, i18n } = useTranslation();
  const { findSubdomain } = useFramework();
  const callApi = useApi();

  const [compose, setCompose] = useState<Compose>(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const byKey = statusByKey(statuses);
  const open = recommendations.filter(
    (r) => effectiveStatus(r.key, byKey) === "open",
  );
  const done = recommendations.filter(
    (r) => effectiveStatus(r.key, byKey) !== "open",
  );

  const label = (subdomainId: string, fallback: string) => {
    const merged = findSubdomain(subdomainId);
    return merged
      ? frameworkLabel(merged, "subdomain", i18n.language, t)
      : fallback;
  };

  const focusText = (rec: Recommendation): string => {
    if (rec.kind === "alert") return t("recommendations.focus.alert");
    if (rec.kind === "risk") {
      return rec.outcome
        ? t("recommendations.focus.risk", { outcome: rec.outcome })
        : t("recommendations.focus.riskNoOutcome");
    }
    return rec.outcome
      ? t("recommendations.focus.change", { outcome: rec.outcome })
      : t("recommendations.focus.changeNoOutcome");
  };

  async function send(
    rec: Recommendation,
    action: RecommendationActionRequest["action"],
    noteText: string | null,
  ) {
    setSaving(true);
    setError(null);
    try {
      const body: RecommendationActionRequest = {
        recommendation_key: rec.key,
        action,
        note: noteText,
        subdomain_id: rec.subdomainId || null,
        state_kind_at_action: rec.stateKind,
      };
      await callApi(`/whanau/${String(whanauId)}/recommendations`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      setCompose(null);
      setNote("");
      onActed();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : t("recommendations.saveError"),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="recs">
      <p className="recs__lead">{t("recommendations.lead")}</p>

      {open.length === 0 ? (
        <div className="recs__empty">
          <p className="recs__empty-title">{t("recommendations.emptyTitle")}</p>
          <p className="recs__empty-body">{t("recommendations.emptyBody")}</p>
        </div>
      ) : (
        <section
          className="recs__group"
          aria-label={t("recommendations.openHeading")}
        >
          <h3 className="recs__group-title">
            {t("recommendations.openHeading")}
          </h3>
          <ul className="recs__list">
            {open.map((rec) => {
              // Narrow to non-null so compose.action is usable below.
              const composeForThis =
                compose && compose.key === rec.key ? compose : null;
              return (
                <li
                  key={rec.key}
                  className={`recs__card recs__card--${rec.kind}`}
                >
                  <div className="recs__card-head">
                    <StateIcon state={rec.stateKind} className="recs__icon" />
                    <span className="recs__name">
                      {label(rec.subdomainId, rec.subdomainNameEn)}
                    </span>
                    <span className={`recs__chip recs__chip--${rec.kind}`}>
                      {t(`recommendations.kind.${rec.kind}`)}
                    </span>
                  </div>

                  {rec.description && (
                    <p className="recs__desc">{rec.description}</p>
                  )}
                  <p className="recs__focus">{focusText(rec)}</p>
                  {rec.outcome && rec.kind !== "alert" && (
                    <p className="recs__goal">
                      <span className="recs__goal-label">
                        {t("recommendations.goalLabel")}:
                      </span>{" "}
                      {rec.outcome}
                    </p>
                  )}

                  {composeForThis ? (
                    <div className="recs__compose">
                      <label
                        className="recs__note-label"
                        htmlFor={`recs-note-${rec.key}`}
                      >
                        {t("recommendations.noteLabel")}
                      </label>
                      <textarea
                        id={`recs-note-${rec.key}`}
                        className="recs__note"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder={t("recommendations.notePlaceholder")}
                        rows={2}
                      />
                      <div className="recs__compose-actions">
                        <button
                          type="button"
                          className="recs__btn recs__btn--primary"
                          disabled={saving}
                          onClick={() =>
                            void send(
                              rec,
                              composeForThis.action,
                              note.trim() || null,
                            )
                          }
                        >
                          {saving
                            ? t("recommendations.saving")
                            : t("recommendations.save")}
                        </button>
                        <button
                          type="button"
                          className="recs__btn"
                          disabled={saving}
                          onClick={() => {
                            setCompose(null);
                            setNote("");
                            setError(null);
                          }}
                        >
                          {t("common.cancel", { defaultValue: "Cancel" })}
                        </button>
                      </div>
                      {error && (
                        <p className="recs__error" role="status">
                          {error}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="recs__actions">
                      <button
                        type="button"
                        className="recs__btn recs__btn--primary"
                        onClick={() => {
                          setCompose({ key: rec.key, action: "actioned" });
                          setNote("");
                          setError(null);
                        }}
                      >
                        {t("recommendations.markActioned")}
                      </button>
                      <button
                        type="button"
                        className="recs__btn"
                        onClick={() => {
                          setCompose({ key: rec.key, action: "dismissed" });
                          setNote("");
                          setError(null);
                        }}
                      >
                        {t("recommendations.dismiss")}
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {done.length > 0 && (
        <section
          className="recs__group recs__group--done"
          aria-label={t("recommendations.doneHeading")}
        >
          <h3 className="recs__group-title">
            {t("recommendations.doneHeading")}
          </h3>
          <ul className="recs__list">
            {done.map((rec) => {
              const status = byKey.get(rec.key);
              const isActioned = effectiveStatus(rec.key, byKey) === "actioned";
              return (
                <li key={rec.key} className="recs__card recs__card--done">
                  <div className="recs__card-head">
                    <span className="recs__name">
                      {label(rec.subdomainId, rec.subdomainNameEn)}
                    </span>
                    <span className="recs__status">
                      {isActioned
                        ? t("recommendations.statusActioned", {
                            date: status ? formatDate(status.acted_at) : "",
                          })
                        : t("recommendations.statusDismissed", {
                            date: status ? formatDate(status.acted_at) : "",
                          })}
                    </span>
                  </div>
                  {status?.note && (
                    <p className="recs__done-note">
                      {t("recommendations.withNote", { note: status.note })}
                    </p>
                  )}
                  <div className="recs__actions">
                    <button
                      type="button"
                      className="recs__btn"
                      disabled={saving}
                      onClick={() => void send(rec, "reopened", null)}
                    >
                      {t("recommendations.reopen")}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {strengths.length > 0 && (
        <section
          className="recs__strengths"
          aria-label={t("recommendations.strengthsHeading")}
        >
          <h3 className="recs__group-title">
            {t("recommendations.strengthsHeading")}
          </h3>
          <p className="recs__strengths-lead">
            {t("recommendations.strengthsLead")}
          </p>
          <ul className="recs__strengths-list">
            {strengths.map((s) => (
              <li key={s.subdomainId} className="recs__strength">
                <StateIcon state="protective" className="recs__icon" />
                <span className="recs__name">
                  {label(s.subdomainId, s.subdomainNameEn)}
                </span>
                {s.outcome && (
                  <span className="recs__strength-outcome">{s.outcome}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export default RecommendationsPanel;
